/**
 * "Is a tool card already showing this turn's progress?"
 *
 * The transcript has two ways to say the agent is busy: a tool card that
 * renders its own running state, and the "Thinking…" indicator at the foot of
 * the list. Exactly one should be visible, so the indicator is suppressed
 * while a tool is running.
 *
 * The suppression used to be `lastMessage.parts.length > 0`, which is a
 * different question. Parts accumulate and never go away, so from the FIRST
 * tool call of a turn to the end of that turn the answer was permanently yes —
 * including during the long stretches when no tool is running at all.
 *
 * That gap is real and it is not short. `tool-call-done` is published BEFORE
 * the worker's `afterToolCall` hook runs (features/agent/core/processor.ts),
 * and for `bash` that hook is the whole-tree `diffAndSync`. By the time the
 * sync starts, every tool-call part is already `completed` and the tool-result
 * part is already appended — so the card shows a finished tool, the indicator
 * is suppressed, and a sync that can run for minutes on a freshly cloned
 * repository produces no visible sign of life at all. Users read it as "the
 * turn finished" and start typing.
 *
 * So ask the question the suppression actually means: is a tool call still in
 * flight? A tool-call part carries its own status and the client reducer
 * flips it on `tool-call-done`, so the answer is on the parts already.
 */
import type { MessagePart } from './hooks.js'

interface TurnMessage {
  role: string
  parts: MessagePart[]
}

/**
 * True when the last message is an assistant message with at least one tool
 * call that has not finished — i.e. a tool card is rendering its own spinner
 * and a second indicator would be a duplicate.
 *
 * Deliberately reads the LAST message only, matching the previous behaviour:
 * a tool call left `running` on an earlier message is a bookkeeping leak, and
 * letting one suppress the indicator for the rest of the session is exactly
 * the failure this function exists to remove.
 */
export function hasRunningToolCall(messages: readonly TurnMessage[]): boolean {
  const last = messages[messages.length - 1]
  if (!last || last.role !== 'assistant') return false
  return last.parts.some(
    p => p.type === 'tool-call' && (p.status === 'running' || p.status === 'pending'),
  )
}

/** What the transcript's activity indicator says. Named `TurnActivity` in
 *  `hooks.ts`; re-declared structurally here so this module stays free of the
 *  hook's imports and can be unit-tested on its own. */
export interface TurnActivityLike {
  kind: 'sync'
  phase: 'walk' | 'hash' | 'sync' | 'delete'
  done: number
  total: number
}

/**
 * The indicator's label.
 *
 * Deliberately says what is happening to the user's files, not what the code is
 * doing: "Saving" rather than "diffAndSync", "your drive" rather than "VFS".
 *
 * The counts are LIVENESS, not a prediction. `sync` walks every known file and
 * most are unchanged, so the pass can jump from 2,000 to 40,000 in a second and
 * then sit still — which is why no phase renders a percentage or an ETA. A
 * progress bar here would be a promise the numbers cannot keep.
 */
export function describeTurnActivity(activity: TurnActivityLike | null): string | null {
  if (!activity || activity.kind !== 'sync') return null
  const n = (v: number) => v.toLocaleString()
  switch (activity.phase) {
    // The walk has already finished by the time it reports (a directory walk
    // has no denominator until it does), so this is past tense on purpose.
    case 'walk': return `Scanned ${n(activity.total)} files`
    case 'hash': return `Checking ${n(activity.done)} of ${n(activity.total)} files`
    case 'sync': return `Saving to your drive — ${n(activity.done)} of ${n(activity.total)} files`
    case 'delete': return `Removing ${n(activity.done)} of ${n(activity.total)} files`
  }
}
